import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ThemeProvider, useTheme, Topbar, PageHeader } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

const ROLE_COLOR = {
  authority: COLORS.green, admin: COLORS.amber, super_admin: COLORS.amber,
  faculty: COLORS.cyan, student: COLORS.purple,
};

function ProfilePageInner() {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [passwordMsg, setPasswordMsg] = useState({ text: '', type: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);

  const roleColor = ROLE_COLOR[user?.role] || COLORS.purple;

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg({ text: '', type: '' });
    if (passwordForm.newPassword.length < 6) { setPasswordMsg({ text: 'Min 6 characters', type: 'error' }); return; }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { setPasswordMsg({ text: 'Passwords do not match', type: 'error' }); return; }
    setPasswordLoading(true);
    try {
      await api.post('/auth/change-password', { new_password: passwordForm.newPassword });
      setPasswordMsg({ text: 'Password changed successfully', type: 'success' });
      setPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordMsg({ text: err.response?.data?.error || 'Failed to change password', type: 'error' });
    } finally { setPasswordLoading(false); }
  };

  const tabStyle = (active) => ({
    padding: '9px 20px', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    background: active ? 'rgba(255,255,255,0.25)' : 'transparent',
    color: active ? '#fff' : 'rgba(255,255,255,0.65)',
    boxShadow: active ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
    transition: 'all 0.2s ease',
  });

  const infoRowStyle = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '13px 16px', borderRadius: 10, marginBottom: 8,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
  };

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={s.bgOrb3} />
      <div style={s.layout}>
        {sidebarOpen && <Sidebar onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar title="My Profile" subtitle="Account settings and information" onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)} />
          <div style={s.content}>
            <PageHeader
              icon="👤"
              title={user?.name || 'My Profile'}
              subtitle={user?.email}
              stats={[
                { label: 'Role', value: user?.role?.replace('_', ' ') },
                { label: 'Department', value: user?.department || 'N/A' },
              ]}
            />

            {/* Profile Header */}
            <div style={{
              ...s.card, marginBottom: 20,
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.25)',
              display: 'flex', alignItems: 'center', gap: 20, padding: '24px',
            }}>
              <div style={{
                width: 72, height: 72, borderRadius: 18, flexShrink: 0,
                background: 'linear-gradient(135deg,rgba(255,255,255,0.3),rgba(255,255,255,0.15))',
                border: '2px solid rgba(255,255,255,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 28, fontWeight: 800, color: '#fff',
                boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                animation: 'scaleIn 0.4s ease-out',
              }}>
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', marginBottom: 4 }}>{user?.name}</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginBottom: 10 }}>{user?.email}</div>
                <span style={{
                  display: 'inline-block', padding: '4px 12px', borderRadius: 20,
                  fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8,
                  background: roleColor + '30', color: roleColor,
                  border: `1px solid ${roleColor}55`,
                }}>
                  {user?.role?.replace('_', ' ')}
                </span>
              </div>
              <button className="ur-btn-hover" style={s.btnDanger} onClick={() => logout(navigate)}>
                Sign Out
              </button>
            </div>

            {/* Tabs */}
            <div style={{
              display: 'flex', gap: 4, marginBottom: 20, padding: '5px',
              borderRadius: 12, background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)', width: 'fit-content',
            }}>
              {['profile', 'security'].map(tab => (
                <button key={tab} style={tabStyle(activeTab === tab)} onClick={() => setActiveTab(tab)}>
                  {tab === 'profile' ? 'Profile Info' : 'Security'}
                </button>
              ))}
            </div>

            {activeTab === 'profile' && (
              <div style={{ ...s.card, animation: 'fadeUp 0.4s ease-out' }}>
                <div style={s.sectionTitle}>Account Information</div>
                <div style={{ marginTop: 16 }}>
                  {[
                    ['Full Name', user?.name],
                    ['Email Address', user?.email],
                    ['Department', user?.department || 'Not specified'],
                    ['Role', user?.role?.replace('_', ' ')],
                    ['Member Since', user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'],
                  ].map(([label, value]) => (
                    <div key={label} style={infoRowStyle}>
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
                      <span style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div style={{ ...s.card, animation: 'fadeUp 0.4s ease-out' }}>
                <div style={s.sectionTitle}>Change Password</div>
                <div style={{ ...s.sectionSub, marginBottom: 20 }}>Update your account password</div>
                <form onSubmit={handleChangePassword} style={{ maxWidth: 420 }}>
                  {passwordMsg.text && (
                    <div style={passwordMsg.type === 'success' ? s.alertOk : s.alertErr}>{passwordMsg.text}</div>
                  )}
                  <div style={{ marginBottom: 16 }}>
                    <label style={s.label}>New Password</label>
                    <input type="password" value={passwordForm.newPassword}
                      onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      placeholder="Min 6 characters" style={s.input} />
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={s.label}>Confirm New Password</label>
                    <input type="password" value={passwordForm.confirmPassword}
                      onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      placeholder="Repeat new password" style={s.input} />
                  </div>
                  <button type="submit" disabled={passwordLoading} className="ur-btn-hover"
                    style={{ ...s.btnPrimary, opacity: passwordLoading ? 0.7 : 1 }}>
                    {passwordLoading ? 'Saving...' : 'Update Password'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProfilePage() {
  return <ThemeProvider><ProfilePageInner /></ThemeProvider>;
}
